import axios from 'axios';

const backendApiUrl = process.env.REACT_APP_BACKEND_API_URL || "https://botmanager-testes.onrender.com";

const api = axios.create({
  baseURL: backendApiUrl,
  timeout: 45000,
  headers: {
    Accept: 'application/json'
  }
});

// ============================================================================
// GERENCIAMENTO DE TOKENS E REFRESH
// ============================================================================

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

// ============================================================================
// INTERCEPTOR: Adiciona Bearer token em requisições
// ============================================================================

api.interceptors.request.use((config) => {
  const accessToken = localStorage.getItem('access_token');

  // ❗ NÃO sobrescrever se já tiver Authorization
  if (!config.headers?.Authorization && accessToken) {
    config.headers = config.headers || {};
    config.headers['Authorization'] = `Bearer ${accessToken}`;
  }

  return config;
});


// ============================================================================
// INTERCEPTOR: Trata 401 e faz refresh automático
// ============================================================================

api.interceptors.response.use(
  response => response,
  async error => {
    const originalRequest = error.config;
    const status = error.response?.status;

    // Não faz refresh para certos endpoints
    const isAuthEndpoint = originalRequest?.url?.includes('/api/auth/');

    // Se recebeu 401 e não é endpoint de auth e não foi tentado antes
    if (status === 401 && !isAuthEndpoint && !originalRequest._retry) {
      originalRequest._retry = true;

      if (isRefreshing) {
        // Enfileira a requisição enquanto refresh está em progresso
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(token => {
          originalRequest.headers['Authorization'] = `Bearer ${token}`;
          return api(originalRequest);
        });
      }

      isRefreshing = true;

      try {
        const refreshToken = localStorage.getItem('refresh_token');
        if (!refreshToken) {
          throw new Error('No refresh token');
        }

        // Tenta fazer refresh
        const response = await axios.post(
          `${backendApiUrl}/api/auth/refresh`,
          null,
          {
            headers: {
              'Authorization': `Bearer ${refreshToken}`
            }
          }
        );

        const { access_token, refresh_token } = response.data;

        if (!access_token) {
          throw new Error('No access token in response');
        }

        // Salva novos tokens
        localStorage.setItem('access_token', access_token);
        if (refresh_token) {
          localStorage.setItem('refresh_token', refresh_token);
        }

        // Atualiza o header da requisição original
        originalRequest.headers['Authorization'] = `Bearer ${access_token}`;

        // Processa requisições enfileiradas
        processQueue(null, access_token);

        // Tenta a requisição original novamente
        return api(originalRequest);

      } catch (err) {
        console.error('[api] Refresh falhou:', err?.message);

        // Só desloga de verdade quando o backend confirma que a sessão é
        // inválida (401: refresh token expirado/revogado/inexistente).
        // Falha de rede, timeout ou 5xx (ex: cold start do backend) é
        // transitória — matar a sessão nesses casos desloga usuários ativos
        // por causa de um blip, mesmo com refresh token válido por 7 dias.
        const isDefinitiveAuthFailure = err.response?.status === 401;

        if (isDefinitiveAuthFailure) {
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
          processQueue(err, null);
          window.location.href = '/login';
        } else {
          processQueue(err, null);
        }

        return Promise.reject(err);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

// ============================================================================
// SERVIÇOS
// ============================================================================

export const dashboardService = {
  getStats: (params) => api.post('/api/dashboard/', params || {}),
  getAdminUsers: (params) => api.get('/api/dashboard/admin/users', { params: params || {} }),
  getAdminPayments: (params) => api.get('/api/dashboard/admin/payments', { params: params || {} }),
  updateUserFee: (userId, data) => api.post(`/api/dashboard/admin/update_fee/${userId}`, data),
  banUser: (userId, data) => api.post(`/api/dashboard/admin/users/${userId}/ban`, data || {}),
  unbanUser: (userId) => api.post(`/api/dashboard/admin/users/${userId}/unban`),
  getAdminBots: (params) => api.get('/api/dashboard/admin/bots', { params: params || {} }),
  // Bots de um usuário (formato de /me) — admin abrindo disparos de bot alheio.
  getAdminUserBots: (userId) => api.get(`/api/dashboard/admin/users/${userId}/bots`),
  blockBot: (botId, data) => api.post(`/api/dashboard/admin/bots/${botId}/block`, data || {}),
  unblockBot: (botId) => api.post(`/api/dashboard/admin/bots/${botId}/unblock`),
  getAdminReports: (params) => api.get('/api/dashboard/admin/reports', { params: params || {} }),
  updateReportStatus: (reportId, data) => api.post(`/api/dashboard/admin/reports/${reportId}/status`, data),
};

export const supportService = {
  createReport: (data) => api.post('/api/support/reports', data),
  myReports: () => api.get('/api/support/reports'),
  reportOptions: () => api.get('/api/support/report-options'),
};

export const statisticsService = {
  getStats: (params) => api.post('/api/statistics/', params || {}),
  getSummary: (params) => api.post('/api/statistics/summary', params || {}),
  getUsers: (params) => api.post('/api/statistics/users', params || {}),
  getPlans: (params) => api.post('/api/statistics/plans', params || {}),
  getHourly: (params) => api.post('/api/statistics/hourly', params || {}),
  getRankings: (params) => api.post('/api/statistics/rankings', params || {}),
  getCountries: (params) => api.post('/api/statistics/countries', params || {}),
  getFull: (params) => api.post('/api/statistics/full', params || {}),
};

export const salesService = {
  getSales: (params) => api.get('/api/sales/', { params: params || {} }),
};

export const landingPageService = {
  getPublicStats: () => api.get('/api/landing-page/')
};

export const authService = {

  // ✅ NOVO: Retorna access + refresh tokens
  register: async (userData) => {
    const response = await api.post('/api/auth/register', userData);
    const { access_token, refresh_token } = response.data;

    if (access_token && refresh_token) {
      localStorage.setItem('access_token', access_token);
      localStorage.setItem('refresh_token', refresh_token);
    }

    return response;
  },

  // ✅ NOVO: Retorna access + refresh tokens
  login: async (credentials) => {
    const response = await api.post('/api/auth/login', credentials);
    const { access_token, refresh_token } = response.data;

    if (access_token && refresh_token) {
      localStorage.setItem('access_token', access_token);
      localStorage.setItem('refresh_token', refresh_token);
    }

    return response;
  },

  // ✅ Retorna dados do usuário autenticado
  me: (config) => {
    const accessToken = localStorage.getItem('access_token');
    if (!accessToken) {
      return Promise.resolve({ data: null });
    }
    return api.get('/api/auth/me', config);
  },

  // ✅ NOVO: Endpoint de refresh manual
  refresh: async () => {
    const refreshToken = localStorage.getItem('refresh_token');
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    try {
      const response = await api.post('/api/auth/refresh', null, {
        headers: {
          'Authorization': `Bearer ${refreshToken}`
        }
      });

      const { access_token, refresh_token } = response.data;

      if (access_token) {
        localStorage.setItem('access_token', access_token);
      }
      if (refresh_token) {
        localStorage.setItem('refresh_token', refresh_token);
      }

      return response;
    } catch (error) {
      // Só apaga a sessão quando o backend confirma que ela é inválida (401).
      // Timeout/rede/5xx é transitório: apagar aqui mandava pro /login no
      // próximo F5 quem só estava com conexão lenta, mesmo com sessão válida.
      if (error?.response?.status === 401) {
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
      }
      throw error;
    }
  },

  // ✅ NOVO: Logout seguro
  logout: async (endpoint) => {
    const refreshToken = localStorage.getItem('refresh_token');

    if (!refreshToken) {
      // Nada pra revogar
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      return;
    }

    try {
      const payload = endpoint ? { endpoint } : null;
      await api.post(
        '/api/auth/logout',
        payload,
        {
          headers: {
            Authorization: `Bearer ${refreshToken}`
          }
        }
      );
    } finally {
      // Sempre limpa local após tentar
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
    }
  },


  change_username: (newUsername) =>
    api.post('/api/auth/change_username', { username: newUsername }),

  update_public_revenue: (payload) =>
    api.post('/api/auth/public_revenue', payload),

  change_password: (currentPassword, newPassword) =>
    api.post('/api/auth/change_password', { current_password: currentPassword, new_password: newPassword }),

  // 🖥️ Sessões ativas (refresh tokens). Manda o refresh token no header
  // pra o backend marcar qual é a sessão "Atual".
  list_sessions: () =>
    api.get('/api/auth/sessions', {
      headers: {
        'X-Refresh-Token': localStorage.getItem('refresh_token') || '',
      },
    }),

  revoke_session: (sessionId) =>
    api.post('/api/auth/sessions/revoke', { session_id: sessionId }),

  profile: () => api.get('/api/auth/profile'),

  // 🔐 Recuperação de senha
  forgotPassword: (email) =>
    api.post('/api/auth/forgot-password', { email }),

  resetPassword: (token, newPassword) =>
    api.post('/api/auth/reset-password', {
      token,
      new_password: newPassword
    }),

  // 📧 Verificação de email
  verifyEmail: async (token) => {
    const response = await api.post('/api/auth/verify-email', { token });
    const { access_token, refresh_token } = response.data || {};
    if (access_token && refresh_token) {
      localStorage.setItem('access_token', access_token);
      localStorage.setItem('refresh_token', refresh_token);
    }
    return response;
  },

  resendVerification: (email) =>
    api.post('/api/auth/resend-verification', { email }),



  push: {
    subscribe: (subscription) => api.post('/api/push/subscribe', subscription),
    unsubscribe: (endpoint) => api.post('/api/push/unsubscribe', { endpoint }),
    updatePreferences: (prefs) => api.post('/api/auth/push_preferences', prefs),
  },

  leads: {
    summary: () => api.get('/api/leads/summary'),
    list: (params) => api.get('/api/leads/', { params }),
    photos: (leads) => api.post('/api/leads/photos', { leads }),
  },

  ranking: () => api.get(`/api/ranking/`),

  gateways: {
    list_gateways: () => api.get('/api/gateways/'),
    saveToken: (gatewayName, credentials) => api.post(`/api/gateways/token/${gatewayName}`, credentials),
    // Duas filas independentes: PIX (order) e Cartão (card_order).
    reorder: (order, cardOrder = []) => api.post('/api/gateways/reorder', { order, card_order: cardOrder }),
    // Desativa um gateway PIX (sai da fila de fallback; só se não for o padrão).
    deactivate: (gatewayName) => api.post(`/api/gateways/deactivate/${gatewayName}`),
    // Stripe Connect (cartão): OAuth "conectar conta existente" + moedas.
    stripeConnect: () => api.get('/api/gateways/stripe/connect'),
    stripeDisconnect: () => api.post('/api/gateways/stripe/disconnect'),
    // Marcas de cartão por credencial (Eagle...): formulário vem de credential_fields.
    saveCardCredentials: (gatewayName, credentials) => api.post(`/api/gateways/card/${gatewayName}/credentials`, credentials),
    disconnectCard: (gatewayName) => api.post(`/api/gateways/card/${gatewayName}/disconnect`),
    stripeSetCurrencies: (currencies) => api.post('/api/gateways/stripe/currencies', { currencies }),
    // Cooud Connect (cartão): conectar conta existente por convite + verificação.
    cooudConfig: () => api.get('/api/gateways/cooud/config'),
    cooudInvite: (email, storeCurrency) => api.post('/api/gateways/cooud/invite', { email, store_currency: storeCurrency }),
    cooudSync: () => api.post('/api/gateways/cooud/sync'),
    cooudOnboardingLink: () => api.post('/api/gateways/cooud/onboarding-link'),
    cooudSaveSettings: (currencies, storeCurrency) => api.post('/api/gateways/cooud/settings', { currencies, store_currency: storeCurrency }),
    cooudDisconnect: () => api.post('/api/gateways/cooud/disconnect'),
    // Prova de posse por código no e-mail da conta Cooud (conta que já estava na plataforma).
    cooudOwnershipStart: (email) => api.post('/api/gateways/cooud/ownership/start', { email }),
    cooudOwnershipConfirm: (code) => api.post('/api/gateways/cooud/ownership/confirm', { code }),
    // Escopo do gateway: 'global' (todos os bots) ou 'specific' (bots escolhidos).
    saveScope: (gatewayName, scope, botIds) => api.post(`/api/gateways/scope/${gatewayName}`, { scope, bot_ids: botIds }),
  },

  tracking: {
    list_tracking: () => api.get('/api/tracking/'),
    create_tracking_profile: (data) => api.post('/api/tracking/profile', data),
    delete_profile: (profileId) => api.delete(`/api/tracking/profile/${profileId}`),
    test_pixel: (profileId) => api.post('/api/tracking/test-pixel', { tracking_profile_id: profileId }),
  },

  redirects: {
    list_redirects: (params) => api.get('/api/redirects/', { params }),

    get_url_utm: (slug) =>
      api.get("/api/redirects/get_url_utm", {
        params: { slug },
      }),

    create_redirect: (data) => api.post('/api/redirects/create', data),
    update_redirect: (slug, data) => api.put(`/api/redirects/${slug}`, data),
    delete_redirect: (id) => api.delete(`/api/redirects/delete/${id}`),
    toggle_redirect: (slug, active) => api.patch(`/api/redirects/${slug}/toggle`, { active }),
    get_stats: (slug) => api.get(`/api/redirects/${slug}/stats`),
    get_metrics_batch: (slugs) => api.post('/api/redirects/metrics', { slugs }),
    // `domain`: domínio escolhido para o link (sem ele, o padrão).
    get_campaign: (slug, domain) => api.get(`/api/redirects/${slug}/campaign`, { params: domain ? { domain } : {} }),
    get_interstitial: (slug) => api.get(`/api/redirects/${slug}/interstitial`),
    update_interstitial: (slug, data) => api.put(`/api/redirects/${slug}/interstitial`, data),
    upload_interstitial_avatar: (slug, file) => {
      const formData = new FormData();
      formData.append('file', file);
      return api.post(`/api/redirects/${slug}/interstitial/avatar`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
    },
    interstitial_avatar_from_bot: (slug, botId) => api.post(`/api/redirects/${slug}/interstitial/avatar/from-bot`, { bot_id: botId }),
    delete_interstitial_avatar: (slug) => api.delete(`/api/redirects/${slug}/interstitial/avatar`),
    // Checa se a URL responde antes de salvar (pode levar alguns segundos)
    update_block_response: (slug, data) => api.put(`/api/redirects/${slug}/block-response`, data, { timeout: 45000 }),
    // Domínios do link: os da Spectrum + o próprio do usuário (com status e DNS).
    list_domains: () => api.get('/api/redirects/domains'),
    add_domain: (hostname) => api.post('/api/redirects/domains', { hostname }),
    verify_domain: (id) => api.post(`/api/redirects/domains/${id}/verify`),
    delete_domain: (id) => api.delete(`/api/redirects/domains/${id}`),
    // Admin: domínios da Spectrum (servem todos os redirecionadores).
    admin_list_domains: () => api.get('/api/redirects/admin/domains'),
    admin_add_domain: (hostname) => api.post('/api/redirects/admin/domains', { hostname }),
    admin_set_domain_active: (id, active) => api.patch(`/api/redirects/admin/domains/${id}`, { active }),
    // Data em que o domínio da Spectrum expira ('AAAA-MM-DD' ou null).
    admin_set_domain_expiry: (id, expiresAt) => api.patch(`/api/redirects/admin/domains/${id}`, { expires_at: expiresAt || null }),
    // IP do servidor de redirect (instrução de DNS do domínio próprio) — painel, não .env.
    admin_get_settings: () => api.get('/api/redirects/admin/settings'),
    admin_save_settings: (serverIps) => api.put('/api/redirects/admin/settings', { server_ips: serverIps }),
  },

  bots: {
    create_bot: (data) => api.post('/api/bots/create/v2/', data),
    edit_bot: (bot_id) => api.get(`/api/bots/edit/${bot_id}`),
    edit_bot_submit: (botId, data) => api.put(`/api/bots/edit/${botId}`, data),
    update_token: (botId, token) => api.post(`/api/bots/${botId}/update-token`, { token }),
    toggle_flow: (botId, isActive) => api.put(`/api/bots/${botId}/toggle_flow`, { is_active_flow: isActive }),
    manage: (bots) => api.post('/api/bots/manage', { bots }),
    // Timeout bem maior que o default: copiar mídias baixa/re-sobe cada arquivo do bot
    // de origem para o destino, e um bot pode ter muitas mídias configuradas.
    import_config: (targetBotId, configKey, copyMedia = false) => api.post('/api/bots/import_config', { target_bot_id: targetBotId, config_key: configKey, copy_media: copyMedia }, { timeout: 600000 }),
    set_config_public: (botId, value) => api.put(`/api/bots/${botId}/config_public`, { config_public: value }),
    save_pix_config: (botId, data) => api.put(`/api/bots/${botId}/pix-config`, data),
    // Foto do topo dos checkouts da Spectrum (já recortada no painel).
    get_checkout_avatar: (botId) => api.get(`/api/bots/${botId}/checkout-avatar`, { responseType: 'blob' }),
    upload_checkout_avatar: (botId, blob) => {
      const formData = new FormData();
      formData.append('file', blob, 'checkout.png');
      return api.post(`/api/bots/${botId}/checkout-avatar`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
    },
    delete_checkout_avatar: (botId) => api.delete(`/api/bots/${botId}/checkout-avatar`),
    get_bot_photo: (botId) => api.get(`/api/bots/${botId}/photo`),
    get_bot_photos: (botIds) => api.post('/api/bots/photos', { bot_ids: botIds }),
    sync_bot_profile: (botId) => api.post(`/api/bots/${botId}/sync-profile`),
    update_bot_profile: (botId, data) => api.put(`/api/bots/${botId}/profile`, data),
    update_bot_photo: (botId, formData) => api.put(`/api/bots/${botId}/profile/photo`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
    delete_bot_photo: (botId) => api.delete(`/api/bots/${botId}/profile/photo`),
  },

  uploads: {
    get_upload: (uploadId) => api.get(`/api/uploads/${uploadId}`),
    upload_media: (botId, file, context = 'generic') => {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('bot_id', botId);
      formData.append('context', context);
      // Não setar Content-Type manualmente: o browser gera o boundary correto para multipart/form-data
      // Uploads síncronos (disparo/welcome/orderbump/upsell/downsell) bloqueiam o
      // request até o Telegram confirmar o file_id, e o backend ainda faz retries.
      // O timeout global (45s) era curto demais e abortava uploads que iriam
      // concluir — por isso "falhava" com frequência. Damos 60s só para o upload.
      return api.post('/api/uploads/upload', formData, { timeout: 60000 });
    },
    // Attach de order bump/disparo multi-bot baixa e re-sobe a mídia (síncrono)
    // para cada bot secundário dentro da mesma request — mesma razão do timeout
    // estendido do upload acima.
    attach_media: (data) => api.post('/api/uploads/attach', data, { timeout: 60000 }),
    detach_media: (targetType, targetId, slot, mediaType, fileId = null) =>
      api.post('/api/uploads/detach', {
        target_type: targetType,
        target_id: targetId,
        slot: slot,
        media_type: mediaType,
        ...(fileId ? { file_id: fileId } : {}),
      }),
    telegram_preview: (fileId, botId, config = {}) =>
      api.get(`/api/uploads/telegram-preview`, {
        params: { file_id: fileId, bot_id: botId },
        responseType: 'blob',
        ...config,
      }),
  },

  validateTelegramBot: (botId, { vip_id, logs_id } = {}) => {
    const params = {};
    if (vip_id) params.vip_id = vip_id;
    if (logs_id) params.logs_id = logs_id;
    return api.get(`/api/bots/validate/${botId}`, { params });
  },

  listBotGroupChats: (botId) => api.get(`/api/bots/validate/group-list/${botId}`),
  refreshBotGroupChats: (botId) => api.post(`/api/bots/validate/group-list/${botId}/refresh`),

  order_bump: {
    get_default_order_bump: (botId) => api.get(`/api/bots/order_bump/${botId}`, { params: { bot_id: botId } }),
    save_order_bump: (botId, data) => api.put(`/api/bots/order_bump/${botId}`, data),

    // Order bump PERSONALIZADO de uma oferta-pai qualquer (upsell/downsell/disparo).
    get_personalized_order_bump: (parentOfferId) =>
      api.get(`/api/bots/order_bump/offer/${parentOfferId}`),
    save_personalized_order_bump: (parentOfferId, data) =>
      api.put(`/api/bots/order_bump/offer/${parentOfferId}`, data),
  },

  downsell: {
    get_downsell: (botId) => api.get(`/api/bots/downsell/${botId}`),
    save_downsell: (botId, data) => api.put(`/api/bots/downsell/${botId}`, data),
    delete_downsell: (botId, downsellId) => api.delete(`/api/bots/downsell/${botId}/${downsellId}`),
    duplicate_downsell: (botId, downsellId) => api.post(`/api/bots/downsell/${botId}/${downsellId}/duplicate`),
    save_default_delivery: (botId, downsell_default_delivery) => api.put(`/api/bots/downsell/${botId}/default-delivery`, { downsell_default_delivery })
  },

  disparo: {
    // Global endpoints (multi-bot)
    list:     (params)   => api.get('/api/disparos/', { params: params || {} }),
    save:     (data)     => api.put('/api/disparos/', data),
    start:    (offerId)  => api.post(`/api/disparos/start/${offerId}`),
    stop:     (offerId)  => api.post(`/api/disparos/stop/${offerId}`),
    schedule:         (offerId, data) => api.post(`/api/disparos/schedule/${offerId}`, data),
    cancelSchedule:   (offerId)       => api.delete(`/api/disparos/schedule/${offerId}`),
    delete:           (offerId)       => api.delete(`/api/disparos/${offerId}`),
    cleanupCopies:  ()              => api.post('/api/disparos/cleanup-copies'),
    runs:           (offerId)       => api.get(`/api/disparos/${offerId}/runs`),
    // Quantos usuários cada público alcança nos bots escolhidos (passo "Público").
    audienceCounts: (botIds)        => api.get('/api/disparos/audience-counts', { params: { bot_ids: botIds.join(',') } }),
    // Legacy per-bot (mantidos para compatibilidade durante migração)
    get_disparo:   (botId, params) => api.get(`/api/bots/disparo/${botId}`, { params: params || {} }),
    save_disparo:  (botId, data)   => api.put(`/api/bots/disparo/${botId}`, data),
    start_disparo: (offerId)       => api.post(`/api/bots/disparo/start/${offerId}`),
  },

  upsell: {
    get_upsell: (botId, params) => api.get(`/api/bots/upsell/${botId}`, { params: params || {} }),
    save_upsell: (botId, data) => api.put(`/api/bots/upsell/${botId}`, data),
    delete_upsell: (botId, upsellId) => api.delete(`/api/bots/upsell/${botId}/${upsellId}`),
    duplicate_upsell: (botId, upsellId) => api.post(`/api/bots/upsell/${botId}/${upsellId}/duplicate`),
    get_chain_info: (botId) => api.get(`/api/bots/upsell/${botId}/chain-info`),
    reorder: (botId, order) => api.post(`/api/bots/upsell/${botId}/reorder`, { order }),
    toggle_active: (botId, upsellId, isActive) =>
      api.patch(`/api/bots/upsell/${botId}/${upsellId}/active`, { is_active: isActive }),
    save_default_delivery: (botId, upsell_default_delivery) => api.put(`/api/bots/upsell/${botId}/default-delivery`, { upsell_default_delivery }),

    // Order Bump específico de um upsell
    get_upsell_order_bump: (botId, upsellId) =>
      api.get(`/api/bots/upsell/${botId}/${upsellId}/order_bump`),

    save_upsell_order_bump: (botId, upsellId, data) =>
      api.put(`/api/bots/upsell/${botId}/${upsellId}/order_bump`, data),
  },

  flows: {
    listAll: () => api.get('/api/bots/flows/all'),
    list: (botId) => api.get(`/api/bots/flows/${botId}`),
    save: (botId, data) => api.put(`/api/bots/flows/${botId}`, data),
    delete: (botId, flowId) => api.delete(`/api/bots/flows/${botId}/${flowId}`),
    exportFlow: (botId, flowId) => api.post(`/api/bots/flows/${botId}/${flowId}/export`),
    importFlow: (botId, exportCode, name) => api.post(`/api/bots/flows/${botId}/import`, { export_code: exportCode, name: name || null }),
    nodeStats: (botId, flowId) => api.get(`/api/bots/flows/${botId}/${flowId}/node-stats`),
    setFolder: (botId, flowId, folderId) => api.put(`/api/bots/flows/${botId}/${flowId}/folder`, { folder_id: folderId }),
    folders: {
      create: (name, color) => api.post('/api/bots/flows/folders', { name, color }),
      update: (folderId, data) => api.put(`/api/bots/flows/folders/${folderId}`, data),
      delete: (folderId) => api.delete(`/api/bots/flows/folders/${folderId}`),
    },
  },

  webhooks: {
    list: (botId) => api.get(`/api/bots/webhooks/${botId}`),
    create: (botId, data) => api.post(`/api/bots/webhooks/${botId}`, data),
    delete: (botId, webhookId) => api.delete(`/api/bots/webhooks/${botId}/${webhookId}`),
    toggle: (botId, webhookId) => api.post(`/api/bots/webhooks/${botId}/${webhookId}/toggle`),
  },
}

export default api;